import Popup from '../components/Popup.js';
import { getCurrentLanguage, getText } from '../translations/translations.js';

// Fallback titles (used if translations fail to load)
const FALLBACK_TITLES = {
    'cookies': 'Cookie Policy',
    'privacy': 'Privacy Policy',
    'policy': 'Terms of Service',
    'licenses': 'Licenses & Libraries',
    'transparency': 'Transparency',
    'about': 'About Us',
    'contact': 'Contact',
    'support': 'Support Us'

};

/**
 * Gets the translated title for an info page
 * @param {string} pageName - The page name
 * @returns {Promise<string>} The translated title
 */
async function getPageTitle(pageName) {
    const translatedTitle = await getText(`infoPages.titles.${pageName}`);
    return translatedTitle || FALLBACK_TITLES[pageName] || pageName.charAt(0).toUpperCase() + pageName.slice(1);
}

/**
 * Loads and displays an info page in a popup
 * @param {string} pageName - The name of the page (e.g., 'cookies', 'privacy', 'policy')
 * @param {string} language - The language code (e.g., 'en', 'es', 'de'). If not provided, uses current language
 * @param {string} size - The popup size ('small', 'medium', 'large', 'extra-large', 'fullscreen')
 */
export async function showInfoPage(pageName, language = null, size = 'large') {
    try {
        // Use current language if not provided
        if (!language) {
            language = getCurrentLanguage();
        }

        // Construct the path to the info page
        const pagePath = `/translations/info/${language}/${pageName}_${language}.html`;

        // Fetch the content
        const response = await fetch(pagePath);
        if (!response.ok) {
            console.error(`Failed to load ${pageName} page from ${pagePath}. Status: ${response.status}`);
            // Try fallback to English if current language fails
            if (language !== 'en') {
                console.warn(`Trying fallback to English for ${pageName} page`);
                const fallbackPath = `/translations/info/en/${pageName}_en.html`;
                const fallbackResponse = await fetch(fallbackPath);
                if (fallbackResponse.ok) {
                    const content = await fallbackResponse.text();
                    const wrappedContent = `<div class="info-page-content">${content}</div>`;
                    const title = await getPageTitle(pageName);
                    return Popup({ title, content: wrappedContent, size });
                }
            }
            throw new Error(`Failed to load ${pageName} page from ${pagePath}`);
        }

        const content = await response.text();

        // Wrap content in proper CSS class for styling
        const wrappedContent = `<div class="info-page-content">${content}</div>`;

        // Get translated title
        const title = await getPageTitle(pageName);

        // Create popup with the content
        const popup = Popup({
            title,
            content: wrappedContent,
            size,
            onClose: () => {
                // Optional: Add any cleanup logic here
            }
        });

        return popup;

    } catch (error) {
        console.error('Error loading info page:', error);
        // Fallback: show error in a small popup
        return Popup({
            title: 'Error',
            content: `<p>Failed to load ${pageName} page. Please try again later.</p>`,
            size: 'small'
        });
    }
}

/**
 * Convenience functions for common info pages
 */
export const showCookiesPage = (language = null) => showInfoPage('cookies', language, 'large');
export const showPrivacyPage = (language = null) => showInfoPage('privacy', language, 'large');
export const showPolicyPage = (language = null) => showInfoPage('policy', language, 'large');
export const showLicensesPage = (language = null) => showInfoPage('licenses', language, 'large');
export const showTransparencyPage = (language = null) => showInfoPage('transparency', language, 'large');
export const showAboutPage = (language = null) => showInfoPage('about', language, 'large');

/**
 * Creates a styled footer button for info popups
 * @param {Object} options - Button configuration
 * @param {string} options.text - Button text
 * @param {string} options.href - Button href (use '#' for non-link actions)
 * @param {string} options.color - CSS color variable or hex (e.g., 'var(--primary-color, #DC5642)')
 * @param {string} options.id - Optional button ID
 * @returns {HTMLElement} Footer container with button
 */
function createFooterButton({ text, href = '#', color, id }) {
    const footer = document.createElement('div');
    footer.style.cssText = 'width: 100%; display: flex; justify-content: center; padding-top: 8px;';

    const btn = document.createElement('a');
    btn.href = href;
    if (id) btn.id = id;
    btn.style.cssText = `display: flex; align-items: center; justify-content: center; background: ${color}; color: white; padding: 10px 24px; border-radius: 16px 16px 0 0; text-decoration: none; font-weight: 600; font-size: 14px; transition: all 0.2s ease; border: none; cursor: pointer;`;
    btn.textContent = text;

    // Add hover effect
    btn.addEventListener('mouseenter', () => btn.style.opacity = '0.95');
    btn.addEventListener('mouseleave', () => btn.style.opacity = '1');

    footer.appendChild(btn);
    return footer;
}

/**
 * Shows an info page with a sticky footer button
 * @param {Object} options - Configuration options
 * @param {string} options.pageName - The page name (e.g., 'support', 'contact')
 * @param {string} options.buttonTextKey - Translation key for button text (e.g., 'infoPages.support.buttonText')
 * @param {string} options.buttonHrefKey - Translation key for button href (optional, e.g., 'infoPages.contact.email')
 * @param {string} options.buttonHrefPrefix - Prefix for href (e.g., 'mailto:')
 * @param {string} options.buttonColor - Footer button color
 * @param {string} options.buttonId - Footer button ID
 * @param {string} options.size - Popup size
 * @param {string} language - Language code
 */
async function showInfoPageWithFooter(options, language = null) {
    const { pageName, buttonTextKey, buttonHrefKey, buttonHrefPrefix = '', buttonColor, buttonId, size = 'medium' } = options;

    try {
        if (!language) {
            language = getCurrentLanguage();
        }

        const pagePath = `/translations/info/${language}/${pageName}_${language}.html`;

        let content;
        const response = await fetch(pagePath);
        if (!response.ok) {
            if (language !== 'en') {
                const fallbackPath = `/translations/info/en/${pageName}_en.html`;
                const fallbackResponse = await fetch(fallbackPath);
                if (fallbackResponse.ok) {
                    content = await fallbackResponse.text();
                } else {
                    throw new Error(`Failed to load ${pageName} page`);
                }
            } else {
                throw new Error(`Failed to load ${pageName} page`);
            }
        } else {
            content = await response.text();
        }

        // Get translated title and button text
        const title = await getPageTitle(pageName);
        const buttonText = await getText(buttonTextKey) || buttonTextKey;

        // Build button href (may include translated email)
        let buttonHref = '#';
        if (buttonHrefKey) {
            const hrefValue = await getText(buttonHrefKey) || '';
            buttonHref = buttonHrefPrefix + hrefValue;
        }

        const wrappedContent = `<div class="info-page-content">${content}</div>`;
        const footer = createFooterButton({
            text: buttonText,
            href: buttonHref,
            color: buttonColor,
            id: buttonId
        });

        return Popup({
            title,
            content: wrappedContent,
            footer,
            size
        });

    } catch (error) {
        console.error(`Error loading ${pageName} page:`, error);
        return Popup({
            title: 'Error',
            content: `<p>Failed to load ${pageName} page. Please try again later.</p>`,
            size: 'small'
        });
    }
}

/**
 * Shows the contact page with a sticky footer email button
 */
export const showContactPage = (language = null) => showInfoPageWithFooter({
    pageName: 'contact',
    buttonTextKey: 'infoPages.contact.buttonText',
    buttonHrefKey: 'infoPages.contact.email',
    buttonHrefPrefix: 'mailto:',
    buttonColor: 'var(--quaternary-color, #326c8e)',
    buttonId: 'contact-email-btn',
    size: 'large'
}, language);
/**
 * Shows the support/donation page with a sticky footer button
 */
export const showSupportPage = (language = null) => showInfoPageWithFooter({
    pageName: 'support',
    buttonTextKey: 'infoPages.support.buttonText',
    buttonColor: 'var(--primary-color, #DC5642)',
    buttonId: 'support-donate-btn',
    size: 'large'
}, language);
